// 解决方案数据标准化
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'
import { resolveBodyHtml } from '@/components/shared/utils/resolveBodyHtml'
import { normalizeContentAttachments } from '@/components/shared/utils/contentAttachments'
import { parseShowAttachments } from '@/components/shared/utils/contentDisplayFlags'
import {
  extractSegmentUnderRoot,
  getMenuNodePath,
  isPathUnderMenu,
  joinMenuPath,
  normalizeContentPath,
  slugFromMenuPath
} from '@/components/portal/common/utils/menuPath'
import { resolveTemplateId } from '@/components/portal/common/utils/menuTemplateRegistry'
import { isPortalMenuVisible } from '@/components/portal/common/utils/portalMenuVisibility'

export { slugFromMenuPath } from '@/components/portal/common/utils/menuPath'

export const CONTENT_LANDING_MODE_LIST = 'LIST'
export const CONTENT_LANDING_MODE_DIRECT = 'DIRECT'

function normalizeContentLandingMode(value) {
  const mode = String(value || '').trim().toUpperCase()
  if (mode === CONTENT_LANDING_MODE_DIRECT) return CONTENT_LANDING_MODE_DIRECT
  if (mode === CONTENT_LANDING_MODE_LIST) return CONTENT_LANDING_MODE_LIST
  return null
}

/**
 * 当前语言 localeStyles → 中文 localeStyles → 主表 → LIST
 */
export function resolveContentLandingMode(menu, locale, defaultLocale = 'zh-CN') {
  const overlay = locale ? menu?.localeStyles?.[locale]?.contentLandingMode : null
  const fallback = defaultLocale ? menu?.localeStyles?.[defaultLocale]?.contentLandingMode : null
  return normalizeContentLandingMode(overlay)
    || normalizeContentLandingMode(fallback)
    || normalizeContentLandingMode(menu?.contentLandingMode)
    || CONTENT_LANDING_MODE_LIST
}

/** 该二级菜单下已发布的方案内容（按现有列表排序） */
export function getSolutionContentsForMenu(menu, items = []) {
  const menuId = menu?.id
  if (menuId == null) return []
  return (items || []).filter((item) => String(item.menuId) === String(menuId))
}

/** 直达时取当前语言下第一条内容 */
export function resolveDirectSolutionContent(menu, items = []) {
  const list = getSolutionContentsForMenu(menu, items)
  return list[0] || null
}

/** 当前语言配置为 DIRECT 且存在可展示内容时直达正文 */
export function shouldDirectLandSolutionMenu(menu, items = [], locale, defaultLocale = 'zh-CN') {
  if (resolveContentLandingMode(menu, locale, defaultLocale) !== CONTENT_LANDING_MODE_DIRECT) {
    return false
  }
  return Boolean(resolveDirectSolutionContent(menu, items)?.id)
}

function findSolutionsMenu(menus = [], rootMenu = null) {
  if (rootMenu) return rootMenu
  return (menus || []).find((menu) => (
    isPortalMenuVisible(menu)
    && resolveTemplateId(menu.templateId) === 2
  )) || (menus || []).find((menu) => (
    isPortalMenuVisible(menu)
    && menu.contentType === 'SOLUTION'
    && menu.children?.length
  )) || null
}

/** 从 rootMenu 子菜单中按 slug 查找对应菜单节点 */
export function findSolutionMenuChild(rootMenu, slug) {
  if (!rootMenu || !slug) return null

  for (const child of (rootMenu.children || []).filter((item) => isPortalMenuVisible(item))) {
    const childSlug = slugFromMenuPath(child.path) || String(child.id)
    if (childSlug === slug) return child
  }

  return null
}

/** 判断路径是否为解决方案顶层列表页（大方案网格） */
export function isSolutionsListPath(path, rootMenu = null) {
  const rootPath = getMenuNodePath(rootMenu)
  if (!rootPath) return false
  return normalizeContentPath(path) === rootPath
}

/** slug 是否为分类菜单保留字（非 contentId 详情段） */
export function isReservedSolutionSlug(slug, rootMenu = null) {
  if (!slug || !rootMenu) return false
  return Boolean(findSolutionMenuChild(rootMenu, slug))
}

/** 从路径提取分类 slug（/solutions/{menuSlug}） */
export function getSolutionCategoryFromPath(path, rootMenu = null) {
  const rootPath = getMenuNodePath(rootMenu)
  if (!rootPath || !isPathUnderMenu(path, rootPath)) return ''
  if (normalizeContentPath(path) === rootPath) return ''

  const segment = extractSegmentUnderRoot(path, rootMenu?.path)
  if (!segment || !isReservedSolutionSlug(segment, rootMenu)) return ''
  return segment
}

/** 判断路径是否为分类内容列表页 */
export function isSolutionCategoryPath(path, rootMenu = null) {
  return Boolean(getSolutionCategoryFromPath(path, rootMenu))
}

/** 判断路径是否为解决方案详情页（/solutions/{contentId}） */
export function isSolutionDetailPath(path, rootMenu = null) {
  const rootPath = getMenuNodePath(rootMenu)
  if (!rootPath) return false
  if (!isPathUnderMenu(path, rootPath)) return false
  if (normalizeContentPath(path) === rootPath) return false

  const segment = extractSegmentUnderRoot(path, rootMenu?.path)
  if (!segment) return false
  return !isReservedSolutionSlug(segment, rootMenu)
}

/** 从详情路径提取 contentId */
export function extractSolutionDetailId(path, rootMenu = null) {
  if (!isSolutionDetailPath(path, rootMenu)) return ''
  return extractSegmentUnderRoot(path, rootMenu?.path)
}

/** @deprecated 兼容旧引用；分类页返回 categorySlug，详情页返回 contentId */
export function extractSolutionSlug(path, rootMenu = null) {
  const category = getSolutionCategoryFromPath(path, rootMenu)
  if (category) return category
  return extractSolutionDetailId(path, rootMenu)
}

/** 构建解决方案 menuId → 元数据 的映射表 */
export function buildSolutionMenuMap(menus = [], rootMenu = null) {
  const map = new Map()
  const solutionsMenu = findSolutionsMenu(menus, rootMenu)
  if (!solutionsMenu) return map

  for (const child of solutionsMenu.children || []) {
    if (!isPortalMenuVisible(child)) continue

    const slug = slugFromMenuPath(child.path) || String(child.id)
    if (!slug) continue

    const menuName = child.name || ''

    map.set(child.id, {
      slug,
      path: getMenuNodePath(child),
      nav: menuName,
      categoryNav: menuName,
      menuName,
      sortOrder: child.sortOrder ?? 0
    })
  }

  return map
}

/** 构建 content 详情页路径 */
export function buildSolutionDetailPath(item, rootMenu = null) {
  if (!item?.id) return ''
  const base = getMenuNodePath(rootMenu)
  if (!base) return ''
  return joinMenuPath(base, String(item.id))
}

/** 标准化单条解决方案列表项 */
export function normalizeSolutionItem(content, menuMap, rootMenu = null) {
  const menu = menuMap.get(content.menuId) || {}
  const categorySlug = menu.slug || String(content.menuId || content.id)
  const categoryPath = menu.path || ''
  const menuName = menu.menuName || menu.nav || ''
  const rootPath = getMenuNodePath(rootMenu)

  return {
    id: content.id,
    menuId: content.menuId,
    slug: categorySlug,
    categorySlug,
    categoryPath,
    path: buildSolutionDetailPath(content, rootMenu),
    image: resolveFileUrl(content.coverUrl),
    nav: menuName || content.title || '',
    categoryNav: menuName || content.title || '',
    title: content.title || '',
    desc: content.summary || '',
    tags: Array.isArray(content.tags) ? content.tags : [],
    highlights: Array.isArray(content.highlights) ? content.highlights : [],
    relatedProductIds: Array.isArray(content.relatedProductIds) ? content.relatedProductIds : [],
    relatedProductSlugs: Array.isArray(content.relatedProductSlugs) ? content.relatedProductSlugs : [],
    sortOrder: menu.sortOrder ?? content.sortOrder ?? 0
  }
}

/** 批量标准化解决方案列表 */
export function normalizeSolutions(contents = [], menus = [], rootMenu = null) {
  const menuMap = buildSolutionMenuMap(menus, rootMenu)

  return (contents || [])
    .filter((item) => item.type === 'SOLUTION')
    .map((item) => normalizeSolutionItem(item, menuMap, rootMenu))
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

export const normalizeHomeSolutions = normalizeSolutions

/** 顶层大方案卡片（仅使用菜单分类：名称、卡片背景图，不回退 content） */
export function buildSolutionCategoryCards(rootMenu) {
  if (!rootMenu) return []

  return (rootMenu.children || [])
    .filter((child) => isPortalMenuVisible(child))
    .map((child) => {
      const slug = slugFromMenuPath(child.path) || String(child.id)
      const path = getMenuNodePath(child)

      return {
        menuId: child.id,
        slug,
        path,
        title: child.name || '',
        desc: child.summary || child.description || '',
        image: resolveFileUrl(child.cardImageUrl) || resolveFileUrl(child.bannerUrl) || '',
        tags: [],
        sortOrder: child.sortOrder ?? 0
      }
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

function resolveSolutionBodyHtml(content) {
  try {
    return resolveBodyHtml(content?.bodyHtml, content?.bodyJson)
  } catch (error) {
    console.error('[solutions] resolveBodyHtml failed', error)
    return ''
  }
}

/** 标准化解决方案详情数据 */
export function normalizeSolutionDetail(content, listItem = null) {
  if (!content) return null

  return {
    id: content.id,
    menuId: content.menuId,
    slug: listItem?.categorySlug || listItem?.slug || String(content.menuId || content.id),
    categorySlug: listItem?.categorySlug || listItem?.slug || '',
    categoryPath: listItem?.categoryPath || '',
    path: listItem?.path || '',
    image: resolveFileUrl(content.coverUrl) || listItem?.image || '',
    nav: listItem?.nav || content.title || '',
    categoryNav: listItem?.categoryNav || listItem?.nav || content.title || '',
    title: content.title || listItem?.title || '',
    desc: content.summary || listItem?.desc || '',
    content: resolveSolutionBodyHtml(content),
    attachments: normalizeContentAttachments(content.attachments),
    showAttachments: parseShowAttachments(content),
    tags: Array.isArray(content.tags) ? content.tags : (listItem?.tags || []),
    highlights: Array.isArray(content.highlights) ? content.highlights : (listItem?.highlights || []),
    relatedProductIds: Array.isArray(content.relatedProductIds)
      ? content.relatedProductIds
      : (listItem?.relatedProductIds || []),
    relatedProductSlugs: Array.isArray(content.relatedProductSlugs)
      ? content.relatedProductSlugs
      : (listItem?.relatedProductSlugs || []),
    sortOrder: listItem?.sortOrder ?? content.sortOrder ?? 0
  }
}

/** 根据 slug 或 id 查找解决方案详情路径 */
export function findSolutionPathBySlug(items = [], routeKey) {
  if (!routeKey) return ''
  const item = (items || []).find((entry) => (
    String(entry.id) === String(routeKey) || entry.slug === routeKey
  ))
  return item?.path || ''
}

/** 获取解决方案顶层列表页路径 */
export function getSolutionsListPath(rootMenu = null) {
  return getMenuNodePath(rootMenu)
}

/** 判断路径是否属于解决方案栏目（顶层 / 分类 / 详情） */
export function isSolutionsSectionPath(path, rootMenu = null) {
  return isSolutionsListPath(path, rootMenu)
    || isSolutionCategoryPath(path, rootMenu)
    || isSolutionDetailPath(path, rootMenu)
}

/**
 * 解决方案 mega menu 右侧栏：按当前左侧分类展示方案标题（常规字重链接）
 */
export function buildSolutionMegaColumns(category, solutions = []) {
  if (!category?.id) return []

  return (solutions || [])
    .filter((item) => String(item.menuId) === String(category.id))
    .sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'zh-CN'))
    .map((item) => ({
      heading: {
        kind: 'solution',
        name: item.title || '',
        path: item.path || ''
      },
      items: []
    }))
}
